import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Plus, Filter } from "lucide-react";
import axios from "axios";
import Navbar from "../components/landing/Navbar";
import Footer from "../components/landing/Footer";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import { useCart } from "../contexts/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  all: "Tous les produits",
  immunity: "Immunité",
  men: "Homme",
  women: "Femme",
  skincare: "Soin de la peau",
  detox: "Détox",
  joints: "Articulations",
  energy: "Énergie",
  supplement: "Complément",
};

function formatPrice(price) {
  return new Intl.NumberFormat("fr-FR").format(price) + " FCFA";
}

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const scrollRef = useScrollAnimation();

  useEffect(() => {
    async function fetchProducts() {
      try {
        await axios.post(`${API}/seed`);
        const res = await axios.get(`${API}/products`);
        setProducts(res.data);
      } catch (err) {
        console.error("Error fetching products:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchProducts();
  }, []);

  const categories = ["all", ...new Set(products.map((p) => p.category).filter(Boolean))];
  const filtered = activeCategory === "all" ? products : products.filter((p) => p.category === activeCategory);

  return (
    <div ref={scrollRef} className="min-h-screen bg-[#F5F0E8]">
      <Navbar />

      {/* Hero banner */}
      <section className="bg-[#6C3FC5] py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1
            data-testid="products-page-title"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Nos Produits
          </h1>
          <p className="text-white/80 text-base md:text-lg max-w-xl mx-auto">
            Découvrez notre gamme complète de compléments alimentaires premium, organisés par catégorie pour vous aider à trouver le produit idéal.
          </p>
        </div>
      </section>

      {/* Category Filters */}
      <div className="sticky top-20 md:top-28 z-40 bg-[#F5F0E8]/95 backdrop-blur-md border-b border-stone-200/50 py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide" data-testid="category-filters">
            {categories.map((cat) => (
              <button
                key={cat}
                data-testid={`category-filter-${cat}`}
                onClick={() => setActiveCategory(cat)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  activeCategory === cat
                    ? "bg-[#6C3FC5] text-white shadow-lg shadow-[#6C3FC5]/20"
                    : "bg-white text-stone-600 hover:bg-stone-50"
                }`}
              >
                {CATEGORY_LABELS[cat] || cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Products */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse">
                <div className="aspect-square bg-stone-200" />
                <div className="p-5 space-y-3">
                  <div className="h-5 bg-stone-200 rounded w-2/3" />
                  <div className="h-4 bg-stone-100 rounded w-full" />
                  <div className="h-6 bg-stone-200 rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
            {filtered.map((product, idx) => (
              <ProductCard key={product.id} product={product} index={idx} />
            ))}
          </div>
        )}

        {filtered.length === 0 && !loading && (
          <div className="text-center py-20">
            <Filter size={48} className="text-stone-300 mx-auto mb-4" />
            <p className="text-stone-500">Aucun produit dans cette catégorie.</p>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}

function ProductCard({ product, index }) {
  const { addItem } = useCart();
  const [imgError, setImgError] = useState(false);

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className={`fade-up group bg-white rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col`}
    >
      <Link to={`/produit/${product.id}`} className="relative aspect-square bg-stone-50 overflow-hidden block">
        {!imgError ? (
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={() => setImgError(true)}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#6C3FC5]/10 to-[#6C3FC5]/5">
            <span className="text-[#6C3FC5] font-bold text-2xl" style={{ fontFamily: "'Playfair Display', serif" }}>
              {product.short_name}
            </span>
          </div>
        )}
        {product.discount_percentage > 0 && (
          <div className="absolute top-3 left-3 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
            -{product.discount_percentage}%
          </div>
        )}
        {!product.in_stock && (
          <div className="absolute inset-0 bg-stone-900/50 flex items-center justify-center">
            <span className="bg-white text-stone-900 px-4 py-2 rounded-full text-sm font-medium">Rupture de stock</span>
          </div>
        )}
      </Link>

      <div className="p-3 sm:p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between mb-2 gap-2">
          <Link to={`/produit/${product.id}`} className="flex-1 min-w-0">
            <h3 className="text-sm sm:text-lg font-bold text-stone-900 hover:text-[#6C3FC5] transition-colors line-clamp-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              {product.name}
            </h3>
          </Link>
          <span className="hidden sm:inline-block text-xs bg-[#6C3FC5]/10 text-[#6C3FC5] px-2 py-0.5 rounded-full font-medium shrink-0">
            {product.short_name}
          </span>
        </div>

        <p className="hidden sm:block text-stone-600 text-sm mb-4 flex-1 leading-relaxed line-clamp-2">{product.description}</p>

        <div className="mb-3 sm:mb-4">
          <span className="text-[#6C3FC5] font-semibold text-base sm:text-lg block">{formatPrice(product.price)}</span>
          {product.discount_percentage > 0 && product.original_price > 0 && (
            <span className="text-stone-400 text-xs sm:text-sm line-through">{formatPrice(product.original_price)}</span>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-2 mt-auto">
          <button
            onClick={() => addItem(product)}
            disabled={!product.in_stock}
            data-testid={`add-to-cart-${product.id}`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#6C3FC5] hover:bg-[#582FB0] disabled:bg-stone-300 disabled:cursor-not-allowed text-white px-2 sm:px-4 py-2.5 rounded-full text-xs sm:text-sm font-medium transition-all duration-200 hover:scale-105"
          >
            <Plus size={14} />
            Ajouter
          </button>
          <Link
            to={`/produit/${product.id}`}
            className="inline-flex items-center justify-center px-3 sm:px-4 py-2 sm:py-2.5 rounded-full border-2 border-[#6C3FC5] text-[#6C3FC5] hover:bg-[#6C3FC5] hover:text-white transition-all duration-200 text-xs sm:text-sm font-medium"
          >
            Détails
          </Link>
        </div>
      </div>
    </div>
  );
}
